import React, { useRef, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Printer, Minus, Plus } from 'lucide-react';
import JsBarcode from 'jsbarcode';

const BarcodePrintDialog = ({ open, onOpenChange, barcode, itemName, orgName, orgPhone }) => {
  const barcodeRef = useRef(null);
  const [rendered, setRendered] = useState(false);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (!open) { setRendered(false); setQty(1); return; }
    const timer = setTimeout(() => {
      if (barcodeRef.current && barcode) {
        try {
          JsBarcode(barcodeRef.current, barcode, {
            format: 'EAN13',
            width: 1.3,
            height: 38,
            displayValue: true,
            fontSize: 10,
            margin: 0,
            textMargin: 1,
            flat: true,
          });
          setRendered(true);
        } catch {
          try {
            JsBarcode(barcodeRef.current, barcode, {
              format: 'CODE128',
              width: 1.3,
              height: 38,
              displayValue: true,
              fontSize: 10,
              margin: 0,
              textMargin: 1,
              flat: true,
            });
            setRendered(true);
          } catch (e2) {
            console.error('Barcode render failed:', e2);
          }
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [open, barcode]);

  const handlePrint = () => {
    const svgEl = barcodeRef.current;
    if (!svgEl) return;

    const orgLine = (orgName || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const phoneLine = (orgPhone || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const labels = Array.from({ length: qty }, () => `
      <div class="label">
        ${orgLine ? `<div class="org-name">${orgLine}${phoneLine ? ` - ${phoneLine}` : ''}</div>` : ''}
        <div class="item-name">${(itemName || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
        <div class="barcode-wrap">${svgEl.outerHTML}</div>
      </div>
    `).join('');

    const win = window.open('', '_blank', 'width=400,height=400');
    win.document.write(`
      <html>
      <head>
        <title>Barcode Label</title>
        <style>
          @page {
            size: 40mm 30mm;
            margin: 0;
          }
          * { box-sizing: border-box; }
          body { margin: 0; padding: 0; }
          .label {
            width: 40mm;
            height: 30mm;
            padding: 1.5mm 2mm 1mm 2mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            page-break-after: always;
            overflow: hidden;
          }
          .label:last-child { page-break-after: auto; }
          .org-name {
            font-family: Arial, sans-serif;
            font-size: 8pt;
            font-weight: bold;
            text-align: center;
            max-width: 36mm;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            margin-bottom: 0.5mm;
            line-height: 1.1;
          }
          .item-name {
            font-family: Arial, sans-serif;
            font-size: 7pt;
            font-weight: bold;
            text-align: center;
            max-width: 36mm;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            margin-bottom: 1mm;
            line-height: 1.1;
          }
          .barcode-wrap {
            display: flex;
            justify-content: center;
          }
          .barcode-wrap svg {
            max-width: 36mm;
            height: auto;
          }
        </style>
      </head>
      <body>${labels}</body>
      </html>
    `);
    win.document.close();
    win.onload = () => { win.print(); win.close(); };
  };

  if (!barcode) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle className="text-sm">Print Barcode Label (40x30mm)</DialogTitle>
        </DialogHeader>

        {/* Preview */}
        <div className="flex flex-col items-center border rounded-lg p-3 bg-white" style={{ width: '151px', height: '113px', margin: '0 auto' }}>
          {orgName && <p className="text-[9px] font-bold text-center text-black truncate w-full">{orgName}{orgPhone ? ` - ${orgPhone}` : ''}</p>}
          <p className="text-[8px] font-bold text-center text-black truncate w-full">{itemName}</p>
          <svg ref={barcodeRef} />
        </div>

        {/* Quantity selector */}
        <div className="flex items-center justify-center gap-3 pt-2">
          <Label className="text-xs">Copies:</Label>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setQty(q => Math.max(1, q - 1))} data-testid="label-qty-minus">
              <Minus className="w-3 h-3" />
            </Button>
            <Input
              type="number"
              min={1}
              max={100}
              value={qty}
              onChange={e => setQty(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
              className="w-14 h-7 text-center text-sm"
              data-testid="label-qty-input"
            />
            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setQty(q => Math.min(100, q + 1))} data-testid="label-qty-plus">
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
          <Button size="sm" onClick={handlePrint} disabled={!rendered} data-testid="print-barcode-btn">
            <Printer className="w-4 h-4 mr-1" /> Print {qty > 1 ? `${qty} Labels` : 'Label'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodePrintDialog;
